import * as React from "react";
import { Hash, User as UserIcon, Megaphone, Radio } from "lucide-react";
import EntityAvatar from "@/components/identity/EntityAvatar";

/**
 * Renders a list of conversations (either channels or DMs). Row shape is
 * deliberately dense — small typography, mono unread badge, one-line preview.
 * For channels, shows kind icon (# channel, 📢 announcement), computed audience
 * chip (site / role / rostered), and the audience label under the name.
 */
export default function ChannelList({
  kind, items, activeId, onSelect, unreadByConv = {}, currentUserId = null,
  emptyLabel = "No conversations.", testidPrefix = "conv",
}) {
  if (!items || items.length === 0) {
    return (
      <div className="px-4 py-3 text-[11.5px] text-brand-faint" data-testid={`${testidPrefix}-empty`}>
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="mt-1" role="list">
      {items.map((c) => {
        const active = c.id === activeId;
        const unread = unreadByConv[c.id] || 0;
        const isAnnouncement = c.kind === "announcement";
        const label = kind === "channel"
          ? `#${c.slug || c.name}`
          : (c.counterpart?.name || "Direct message");
        const audKind = (c.audience_spec || {}).kind;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onSelect(c)}
            className={`w-full flex items-start gap-2 px-3 py-1.5 text-left transition-colors ${
              active ? "bg-accent text-brand-violet" : "hover:bg-brand-hush text-brand-ink"
            }`}
            data-testid={`${testidPrefix}-${c.id}`}
            data-active={active ? "true" : undefined}
            aria-label={label}
          >
            {kind === "dm" ? (
              <EntityAvatar
                kind="person"
                name={c.counterpart?.name || "?"}
                id={c.counterpart?.id || c.id}
                size={20}
                className="mt-0.5"
              />
            ) : (
              <span className={`mt-0.5 h-4 w-4 rounded flex items-center justify-center shrink-0 ${
                active ? "text-brand-violet" : "text-brand-muted"
              }`}>
                {isAnnouncement ? <Megaphone size={11} /> : audKind === "rostered_today" ? <Radio size={11} /> : <Hash size={11} />}
              </span>
            )}
            <span className="flex-1 min-w-0">
              <span className={`flex items-baseline gap-1.5 flex-wrap`}>
                <span className={`text-[12.5px] truncate ${
                  unread > 0 ? "font-semibold" : "font-medium"
                } ${active ? "text-brand-violet" : "text-brand-ink"}`}>
                  {label}
                </span>
                {isAnnouncement && (
                  <span className="mono-num text-[9.5px] chip chip-amber">announcement</span>
                )}
                {audKind && audKind !== "static" && !isAnnouncement && (
                  <span className="mono-num text-[9.5px] chip">
                    {audKind === "rostered_today" ? "rostered" : audKind}
                  </span>
                )}
                {c.archived && (
                  <span className="mono-num text-[9.5px] chip chip-amber">archived</span>
                )}
              </span>
              {c.audience_label && kind === "channel" && audKind !== "static" && (
                <span className="block text-[10.5px] text-brand-muted truncate mt-0.5">
                  {c.audience_label}
                </span>
              )}
              {c.last_message_preview && (
                <span className="block text-[11px] text-brand-muted truncate mt-0.5">
                  {c.last_sender_name ? `${c.last_sender_name.split(" ")[0]}: ` : ""}{c.last_message_preview}
                </span>
              )}
            </span>
            {unread > 0 && (
              <span
                className="mono-num text-[10px] font-semibold rounded-full bg-brand-violet text-white px-1.5 h-4 min-w-[16px] flex items-center justify-center shrink-0"
                data-testid={`${testidPrefix}-${c.id}-unread`}
              >
                {unread}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
